import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import styled from "styled-components";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { useFlag } from "@unleash/proxy-client-react";

import { REQUEST_OTP_LOGIN } from "../../../graphql/resolvers/member";
import { RequestOtpLogin, RequestOtpLoginVariables } from "../../../types/__generated__/RequestOtpLogin";
import Button from "../../atoms/Button";
import { getLocalStorageToken } from "../../../helpers/auth";
import { readAuthToken, writeAuthToken } from "../../../helpers/tokenStorage";
import breakpoints from "../../../styles/breakpoints";
import {
  REDIRECT_URL_PARAM,
  I18N_NAMESPACES,
  UNLEASH_LOGIN_OTP_SIGNUP_REDIRECT,
} from "../../../helpers/constants";
import urlPatterns from "../../../urls";
import { logError } from "../../../helpers/logging";
import { showErrorAlert, showSuccessAlert } from "../../../helpers/alerts";
import InputField from "../../atoms/InputField";
import GlobalAlert from "../../atoms/GlobalAlert/GlobalAlert";
import { StyledInputHolder } from "../../atoms/Input";
import { getGuestCheckoutLoginRedirect } from "../../../helpers/guestCheckoutLoginRedirect";

const StyledFormBtn = styled(Button)`
  margin-bottom: 20px;
  ${breakpoints.smDown} {
    margin-top: 10px;
  }
`;

const EmailWrapper = styled(StyledInputHolder)`
  margin-bottom: 20px;
`;

const LoginFormRequestOTP = () => {
  const history = useHistory();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [genericErrorMessage, setGenericErrorMessage] = useState("");
  const { t } = useTranslation([I18N_NAMESPACES.COMMON]);
  const isOtpSignupRedirectEnabled = useFlag(UNLEASH_LOGIN_OTP_SIGNUP_REDIRECT);

  const [auth, setAuth] = useState({
    accessToken: "",
    refreshToken: "",
    encodedTokens: readAuthToken() || "",
  });

  useEffect(() => {
    const jwToken = auth.encodedTokens && getLocalStorageToken();
    if (jwToken) {
      // @ts-ignore
      setEmail(jwToken?.email ?? "");
      setAuth({
        ...auth,
        // @ts-ignore
        accessToken: jwToken?.accessToken,
        // @ts-ignore
        refreshToken: jwToken?.refreshToken,
      });
    }
  }, []);

  const [requestOtpLogin, { loading: isRequestOtpLoginLoading }] = useMutation<
    RequestOtpLogin,
    RequestOtpLoginVariables
  >(REQUEST_OTP_LOGIN);

  /**
   * Updates the email state.
   */
  // @ts-ignore
  const handleChange = (name: string, value: string) => {
    setEmail(value);
  };

  /**
   * Validates and submits the form.
   * */
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setGenericErrorMessage("");

    // Verifies all fields were filled out
    const hasEmptyFields = email === "";
    if (hasEmptyFields) {
      setGenericErrorMessage(t("common:sorryAllFieldRequired"));
      return false;
    }

    return sendLogInMutation();
  };

  /**
   * Executes login and redirects to my account page if login successful
   * */
  const sendLogInMutation = () => {
    // Removes the persisted token (web + Android localStorage, iOS NSUserDefaults) if already set.
    if (readAuthToken()) {
      writeAuthToken(null);
    }

    const queryParams = new URLSearchParams(window.location.search);
    const redirectUrlFromParams = queryParams.get(REDIRECT_URL_PARAM);
    // @ts-ignore
    const redirectUrl = location?.state?.redirectUrl;

    // @ts-ignore
    const quizData = location?.state?.quizData;

    requestOtpLogin({
      variables: {
        input: {
          email,
        },
      },
    })
      .then(({ data }) => {
        const { isSuccessful, message, errors, accountExists } = data?.requestOtpLogin || {};

        // Account doesn't exist for this email - send the user to sign up instead.
        if (isOtpSignupRedirectEnabled && accountExists === false) {
          setGenericErrorMessage("");
          // Pass the message via route state so the sign-up page can toast it on mount.
          // Firing the toast here would be wiped by the immediate redirect.
          history.push(urlPatterns.SIGN_UP, {
            email,
            redirectUrl: getGuestCheckoutLoginRedirect(
              redirectUrl || redirectUrlFromParams || urlPatterns.MY_DASHBOARD,
              !!quizData
            ),
            quizData,
            signupRedirectMessage: message || t("common:loginPage.noAccountRedirect"),
          });
          return;
        }

        if (isSuccessful) {
          showSuccessAlert(message || t("common:success"), { autoClose: 10000 });
          history.push(urlPatterns.LOGIN_VERIFY_OTP, {
            redirectUrl: getGuestCheckoutLoginRedirect(
              redirectUrl || redirectUrlFromParams || urlPatterns.MY_DASHBOARD,
              !!quizData
            ),
            email,
            quizData,
          });
        }
        if (errors) {
          const error = errors?.[0]?.messages?.[0] || t("common:sorry");
          error && setGenericErrorMessage(error);
        }
      })
      .catch((error) => {
        logError(error);
        showErrorAlert(t("common:sorry"), { autoClose: 10000 });
      });
  };

  return (
    <form onSubmit={(event) => handleSubmit(event)}>
      <EmailWrapper>
        <InputField
          type="email"
          placeholder={t("common:loginPage.email")}
          name="email"
          id="email"
          value={email}
          onChange={handleChange}
        />
      </EmailWrapper>

      {genericErrorMessage && <GlobalAlert message={genericErrorMessage} type="error" />}

      <StyledFormBtn full lg type="submit" disabled={isRequestOtpLoginLoading} whiteBgOnHover>
        {isRequestOtpLoginLoading ? `${t("common:processing")}` : `${t("common:continue")}`}
      </StyledFormBtn>
    </form>
  );
};

export default LoginFormRequestOTP;
